//#region Imports
import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleChevronLeft,
  faTimes,
  faWarning,
} from '@fortawesome/pro-regular-svg-icons';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
  Skeleton,
} from '@mui/material';

import { GlobalVariables } from '../../../../globalVar';
import customAxios from '../../../../utils/customAxios';
import { getToast } from '../../../../utils/toastConfig';
import { useTranslationHook } from '../../../../hooks/UseTranslationHook';
//#region

const HEADERS = {
  headers: {
    Authorization: 'bearer ' + localStorage.getItem('token'),
  },
};

export const DeleteFailureTypeModal = ({
  failureTypeId,
  showModal,
  setShowModal,
  refreshData,
}) => {
  const [isLoading, setIsLoading] = useState(false); // State for tracking deletion process
  const [isFetching, setIsFetching] = useState(false); // State for tracking data fetching
  const [failureType, setFailureType] = useState({
    id: '',
    name: '',
    description: '',
  }); // State for the selected failure type details

  const { modalYesString, modalNoString } = useTranslationHook(); // Translation strings for modal buttons

  useEffect(() => {
    if (showModal) {
      fetchData(); // Fetch region data when the modal is opened
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  /**
   * Fetches the details of the selected region.
   */
  const fetchData = async () => {
    setIsFetching(true);
    const endpoint = `${GlobalVariables.getURL()}failure-types/${failureTypeId}`;

    try {
      const response = await customAxios.get(endpoint, HEADERS);
      const failureType = response.data.data;
      setFailureType({
        id: failureType.id,
        name: failureType.name,
        description: failureType.description,
      }); // Update failure type details
    } catch (error) {
      console.error('Error fetching failure type data:', error);
      getToast({
        status: 'error',
        title: error.response?.data?.message || 'Error',
      });
    } finally {
      setIsFetching(false);
    }
  };

  /**
   * Closes the modal and refreshes the regions data.
   */
  const handleClose = () => {
    setShowModal(false);
    refreshData();
  };

  /**
   * Handles the deletion of the selected region.
   */
  const handleDelete = async () => {
    setIsLoading(true);
    const endpoint = `${GlobalVariables.getURL()}failure-types/delete/${failureTypeId}`;

    try {
      const response = await customAxios.delete(endpoint, HEADERS);

      if (response.status === 200) {
        getToast({
          status: 'success',
          title: 'Tpo de servicio eliminado exitosamente',
        });
        handleClose(); // Close the modal after successful deletion
      }
    } catch (error) {
      console.error('Error deleting failure type:', error);
      getToast({
        status: 'error',
        title: error.response?.data?.message || 'Error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={showModal}
      onClose={handleClose}
      fullWidth={true}
      maxWidth='md'
    >
      <DialogTitle>
        <Typography className='inventory-movmodal-title'>
          Eliminar tipo de servicio
        </Typography>
      </DialogTitle>
      <IconButton
        aria-label='close'
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 20,
          top: 20,
        }}
      >
        <FontAwesomeIcon icon={faTimes} />
      </IconButton>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant='h5'>
              ¿Seguro que deseas eliminar este tipo de servicio?
            </Typography>
          </Grid>
          {/* Display region ID */}
          <Grid item xs={12} md={2}>
            {isFetching ? (
              <Skeleton variant='text' width={100} height={30} />
            ) : (
              <Typography variant='h6'>
                <span style={{ fontWeight: 'bold' }}>ID: </span>
                {failureType.id}
              </Typography>
            )}
          </Grid>
          {/* Display region name */}
          <Grid item xs={12} md={10}>
            {isFetching ? (
              <Skeleton variant='text' width={300} height={30} />
            ) : (
              <Typography variant='h6'>
                <span style={{ fontWeight: 'bold' }}>
                  Nombre del tipo de servicio:
                </span>
                {failureType.name}
              </Typography>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        {/* Confirm Delete Button */}
        <button
          className='modal-button'
          onClick={handleDelete}
          disabled={isLoading}
        >
          <FontAwesomeIcon icon={faWarning} /> {modalYesString}
        </button>
        {/* Cancel Button */}
        <button
          className='modal-button no'
          onClick={handleClose}
          disabled={isLoading}
        >
          <FontAwesomeIcon icon={faCircleChevronLeft} /> {modalNoString}
        </button>
      </DialogActions>
    </Dialog>
  );
};
